import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Icon, type IconName } from './Icon.js';

export interface MenuAction {
  id: string;
  label: string;
  icon: IconName;
  danger?: boolean;
  onSelect: () => void;
}

interface MessageMenuProps {
  /** Viewport coordinates of the right-click. */
  x: number;
  y: number;
  actions: MenuAction[];
  onClose: () => void;
  /** When present, a row of reactions sits above the actions, as in Telegram. */
  reactions?: {
    /** The caller's current reaction, highlighted; choosing it again removes it. */
    current: string | null;
    onReact: (emoji: string) => void;
  };
}

/** The quick row. */
const QUICK_REACTIONS = ['👍', '❤️', '🔥', '😂', '😮', '😢', '👏'];

/** Behind the expander. */
const MORE_REACTIONS = [
  '🎉',
  '🙏',
  '👌',
  '🤝',
  '💯',
  '✅',
  '❌',
  '👀',
  '🤔',
  '😁',
  '🥰',
  '😍',
  '😎',
  '🤩',
  '😅',
  '😐',
  '😕',
  '😡',
  '🤯',
  '😱',
  '🥳',
  '😴',
  '🙌',
  '💪',
  '⚡',
  '⭐',
  '🚀',
  '💡',
  '☕',
  '🐛',
  '👎',
  '🤷',
];

/**
 * The custom right-click menu for a message.
 *
 * Fixed-position at the pointer, nudged back inside the viewport once its size
 * is known. Closes on Esc, an outside press, scrolling, resizing or losing
 * focus. Arrow keys move between items, as in a native menu.
 */
export function MessageMenu({ x, y, actions, onClose, reactions }: MessageMenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: x, top: y });
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const element = ref.current;

    if (!element) {
      return;
    }

    const margin = 8;
    const { width, height } = element.getBoundingClientRect();

    setPosition({
      left: Math.max(margin, Math.min(x, window.innerWidth - width - margin)),
      top: Math.max(margin, Math.min(y, window.innerHeight - height - margin)),
    });
    element.querySelector<HTMLButtonElement>('.menu-item')?.focus();
  }, [x, y, expanded]);

  useEffect(() => {
    function onPointerDown(event: PointerEvent): void {
      if (!ref.current?.contains(event.target as Node)) {
        onClose();
      }
    }

    function onKeyDown(event: globalThis.KeyboardEvent): void {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    }

    // Capture phase, so a scroll inside the transcript closes it too.
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    window.addEventListener('blur', onClose);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose]);

  function moveFocus(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }

    event.preventDefault();
    const items = [...(ref.current?.querySelectorAll<HTMLButtonElement>('.menu-item') ?? [])];
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    items[(index + step + items.length) % items.length]?.focus();
  }

  return (
    <div
      ref={ref}
      className="context-menu"
      role="menu"
      style={position}
      onKeyDown={moveFocus}
      onContextMenu={(event) => event.preventDefault()}
    >
      {reactions ? (
        <div className={expanded ? 'reaction-picker reaction-picker--open' : 'reaction-picker'}>
          {(expanded ? [...QUICK_REACTIONS, ...MORE_REACTIONS] : QUICK_REACTIONS).map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={
                emoji === reactions.current ? 'reaction-pick reaction-pick--mine' : 'reaction-pick'
              }
              aria-label={emoji === reactions.current ? `Remove ${emoji}` : `React with ${emoji}`}
              onClick={() => {
                onClose();
                reactions.onReact(emoji);
              }}
            >
              {emoji}
            </button>
          ))}
          {expanded ? null : (
            <button
              type="button"
              className="reaction-pick reaction-more"
              aria-label="More reactions"
              onClick={() => setExpanded(true)}
            >
              <Icon name="chevronDown" size={15} />
            </button>
          )}
        </div>
      ) : null}

      {actions.map((action) => (
        <button
          key={action.id}
          type="button"
          role="menuitem"
          className={action.danger ? 'menu-item menu-item--danger' : 'menu-item'}
          onClick={() => {
            onClose();
            action.onSelect();
          }}
        >
          <Icon name={action.icon} size={15} />
          {action.label}
        </button>
      ))}
    </div>
  );
}
